import { NodeViewWrapper } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";
import { useEffect, useRef, useState, useCallback } from "react";
import mermaid from "mermaid";
import { Edit3, Eye, Trash2, AlertCircle } from "lucide-react";

// Initialize mermaid once
let mermaidInitialized = false;
const initializeMermaid = () => {
  if (!mermaidInitialized) {
    mermaid.initialize({
      startOnLoad: false,
      theme: "default",
      securityLevel: "loose",
      fontFamily: "inherit",
    });
    mermaidInitialized = true;
  }
};

export function MermaidNodeView({
  node,
  updateAttributes,
  deleteNode,
  selected,
}: NodeViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editCode, setEditCode] = useState(node.attrs.code || "");
  const [isRendering, setIsRendering] = useState(false);

  const renderDiagram = useCallback(async () => {
    if (!containerRef.current || !node.attrs.code) return;

    initializeMermaid();
    setIsRendering(true);
    setError(null);

    try {
      const code = node.attrs.code.trim();
      const id = `mermaid-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      const { svg } = await mermaid.render(id, code);

      if (containerRef.current) {
        containerRef.current.innerHTML = svg;
        // Make SVG responsive
        const svgEl = containerRef.current.querySelector("svg");
        if (svgEl) {
          svgEl.style.maxWidth = "100%";
          svgEl.style.height = "auto";
        }
      }
    } catch (err) {
      console.error("Mermaid render error:", err);
      setError(
        err instanceof Error ? err.message : "Error al renderizar diagrama",
      );
    } finally {
      setIsRendering(false);
    }
  }, [node.attrs.code]);

  useEffect(() => {
    if (!isEditing) {
      renderDiagram();
    }
  }, [isEditing, renderDiagram]);

  const handleSave = () => {
    updateAttributes({ code: editCode });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditCode(node.attrs.code || "");
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      handleCancel();
    } else if (e.key === "Enter" && e.metaKey) {
      handleSave();
    }
  };

  return (
    <NodeViewWrapper
      className={`mermaid-node-wrapper my-4 ${selected ? "ring-2 ring-primary-500 ring-offset-2" : ""}`}
    >
      <div className="relative bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-3 py-2 bg-gray-100 border-b border-gray-200">
          <span className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 3h18v18H3V3z" />
              <path d="M3 9h18" />
              <path d="M9 21V9" />
            </svg>
            Diagrama Mermaid
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => {
                if (isEditing) {
                  handleCancel();
                } else {
                  setIsEditing(true);
                }
              }}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
              title={isEditing ? "Vista previa" : "Editar codigo"}
            >
              {isEditing ? (
                <Eye className="w-4 h-4" />
              ) : (
                <Edit3 className="w-4 h-4" />
              )}
            </button>
            <button
              type="button"
              onClick={deleteNode}
              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Eliminar diagrama"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        {isEditing ? (
          <div className="p-3">
            <textarea
              value={editCode}
              onChange={(e) => setEditCode(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full h-48 p-3 font-mono text-sm bg-white border border-gray-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Escribe el codigo Mermaid aqui..."
              spellCheck={false}
            />
            <div className="flex items-center justify-between mt-3">
              <a
                href="https://mermaid.js.org/syntax/flowchart.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary-600 hover:text-primary-700 hover:underline"
              >
                Ver documentacion de Mermaid
              </a>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="px-3 py-1.5 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                >
                  Guardar (Cmd+Enter)
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4">
            {isRendering ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-pulse text-gray-400">
                  Renderizando diagrama...
                </div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <AlertCircle className="w-8 h-8 text-red-500 mb-2" />
                <p className="text-sm text-red-600 font-medium">
                  Error en el diagrama
                </p>
                <p className="text-xs text-red-500 mt-1 max-w-md">{error}</p>
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="mt-3 text-xs text-primary-600 hover:text-primary-700 hover:underline"
                >
                  Editar codigo
                </button>
              </div>
            ) : (
              <div
                ref={containerRef}
                className="flex justify-center items-center min-h-[100px] overflow-x-auto"
              />
            )}
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
}
